import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Badge, Button, Dropdown, IconButton, ProgressBar,
} from '@openedx/paragon';
import { RiArrowRightSLine, RiMoreFill } from '@remixicon/react';
import messages from './messages';
import { Link } from 'react-router-dom';

const OngoingCoursesTable = ({ courses, limit = 4, hideSectionHeader = false }) => {
  const intl = useIntl();

  if (!courses || courses.length === 0) {
    return null;
  }

  const displayCourses = Number.isFinite(limit) ? courses.slice(0, limit) : courses;
  const learningBase = getConfig().LEARNING_BASE_URL || '';
  const progressBaseUrl = learningBase.includes('/learning') ? learningBase : `${learningBase}/learning`;

  return (
    <>
      {!hideSectionHeader && (
        <div className="ongoing-table__section-header">
          <h2 className="ongoing-table__section-title">
            {intl.formatMessage(messages.ongoingCoursesTitle)}
          </h2>
          <Button
            variant="ghost-secondary"
            size="sm"
            iconAfter={RiArrowRightSLine}
            as={Link}
            to="/progress/courses"
          >
            {intl.formatMessage(messages.viewAll)}
          </Button>
        </div>
      )}

      <div className="ongoing-table__card">
        <div className="ongoing-table__content">
          <div className="ongoing-table__header-row">
            <div className="ongoing-table__cell ongoing-table__cell--course">
              {intl.formatMessage(messages.course)}
            </div>
            <div className="ongoing-table__cell ongoing-table__cell--progress">
              {intl.formatMessage(messages.progress)}
            </div>
            <div className="ongoing-table__cell ongoing-table__cell--action">
              {intl.formatMessage(messages.action)}
            </div>
          </div>

          <div className="ongoing-table__body">
            {displayCourses.map((course) => (
              <div className="ongoing-table__row" key={course.id}>
                <div className="ongoing-table__cell ongoing-table__cell--course">
                  {course.homeUrl ? (
                    <a href={course.homeUrl} className="ongoing-table__thumb-link" aria-label={course.title}>
                      <div
                        className="ongoing-table__thumb"
                        style={{ backgroundImage: `url(${course.imageUrl})` }}
                        role="img"
                        aria-label={course.title}
                      />
                    </a>
                  ) : (
                    <div
                      className="ongoing-table__thumb"
                      style={{ backgroundImage: `url(${course.imageUrl})` }}
                      role="img"
                      aria-label={course.title}
                    />
                  )}
                  <div className="ongoing-table__course-text">
                    {course.homeUrl ? (
                      <a href={course.homeUrl} className="ongoing-table__course-name">{course.title}</a>
                    ) : (
                      <span className="ongoing-table__course-name">{course.title}</span>
                    )}
                    {course.certificateStatus === 'expired' && (
                      <Badge variant="soft" color="danger" size="sm">
                        {intl.formatMessage(messages.certificateExpired)}
                      </Badge>
                    )}
                    {course.certificateStatus === 'active' && course.certificateExpiresAt && (
                      <span className="ongoing-table__cert-expiry">
                        {intl.formatMessage(messages.certificateExpires, {
                          date: intl.formatDate(course.certificateExpiresAt, {
                            year: 'numeric', month: 'short', day: 'numeric',
                          }),
                        })}
                      </span>
                    )}
                  </div>
                </div>
                <div className="ongoing-table__cell ongoing-table__cell--progress">
                  <ProgressBar
                    now={course.progressPercent}
                    label={`${course.progressPercent}%`}
                    srOnly
                    variant="success"
                    className="ongoing-table__progress-bar"
                  />
                </div>
                <div className="ongoing-table__cell ongoing-table__cell--action">
                  <Button
                    href={course.certificateUrl || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!course.certificateIsEarned
                      || !course.certificateUrl
                      || course.certificateStatus === 'expired'}
                    variant="ghost-secondary"
                    size="sm"
                  >
                    {intl.formatMessage(messages.viewCertificate)}
                  </Button>
                  <Dropdown>
                    <Dropdown.Toggle
                      id={`ongoing-actions-${course.id}`}
                      as={IconButton}
                      src={RiMoreFill}
                      alt={intl.formatMessage(messages.moreOptions)}
                      size="sm"
                      variant="ghost-secondary"
                    />
                    <Dropdown.Menu>
                      <Dropdown.Item
                        as="a"
                        href={course.courseId ? `${progressBaseUrl}/course/${course.courseId}/progress` : undefined}
                        disabled={!course.courseId}
                      >
                        {intl.formatMessage(messages.viewProgress)}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default OngoingCoursesTable;
